import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { getCheckpointsList } from '../endpoints/checkpoints/list_GET.schema';
import { createCheckpoint } from '../endpoints/checkpoints/create_POST.schema';
import { restoreCheckpoint } from '../endpoints/checkpoints/restore_POST.schema';
import { restoreCheckpointFile } from '../endpoints/checkpoints/restore_file_POST.schema';
import { getCheckpointPreview } from '../endpoints/checkpoints/preview_GET.schema';

export const useCheckpoints = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['checkpoints', 'list', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getCheckpointsList({ sessionId });
    },
    enabled: !!sessionId,
    retry: 1,
    staleTime: 1000 * 10, // 10 seconds — checkpoints created during chat should appear quickly
    refetchOnMount: 'always', // Always refetch when History tab is opened
  });
};

export const useCreateCheckpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCheckpoint,
    onSuccess: (data) => {
      toast.success('Checkpoint created successfully');
      // Show GitHub commit info if it was auto-pushed
      if (data.githubCommit?.success) {
        toast.success(
          <div>
            <strong>Synced to GitHub!</strong>
            <br />
            <a
              href={data.githubCommit.commitUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary)' }}
            >
              View commit {data.githubCommit.commitSha?.substring(0, 7)}
            </a>
          </div>,
          { duration: 5000 },
        );
      } else if (data.githubCommit?.error) {
        toast.warning(`GitHub sync failed: ${data.githubCommit.error}`);
      }
      queryClient.invalidateQueries({
        queryKey: ['checkpoints', 'list', data.checkpoint.sessionId],
      });
    },
    onError: (error) => {
      toast.error(`Failed to create checkpoint: ${error.message}`);
    },
  });
};

export const useRestoreCheckpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: restoreCheckpoint,
    onSuccess: (data, variables) => {
      toast.success(`Restored ${data.restoredFileCount} files from checkpoint`);
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['sessions', 'detail'] });
    },
    onError: (error) => {
      toast.error(`Failed to restore checkpoint: ${error.message}`);
    },
  });
};

export const useRestoreCheckpointFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { checkpointId: string; filename: string }) => {
      return restoreCheckpointFile(input);
    },
    onSuccess: (data) => {
      toast.success(`Restored ${data?.restored?.filename} from checkpoint`);
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['checkpoints', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['checkpoints', 'preview'] });
    },
    onError: (error) => {
      toast.error(`Failed to restore file: ${error.message}`);
    },
  });
};

export const useCheckpointPreview = (checkpointId: string | null) => {
  return useQuery({
    queryKey: ['checkpoints', 'preview', checkpointId],
    queryFn: () => {
      if (!checkpointId) throw new Error('Checkpoint ID is required');
      return getCheckpointPreview({ checkpointId });
    },
    enabled: !!checkpointId,
  });
};
